import React, { useState, useEffect } from "react";
import {
  Key,
  RefreshCcw,
  CheckCircle,
  X,
  Globe,
  Link,
  Upload,
  Download,
  Server,
  Webhook,
  Trash2,
  Plus,
  Settings,
} from "lucide-react";

const STORAGE_KEY = "api_integration_settings_v1";

const defaults = {
  apiKey: "",
  domains: ["https://example.com"],
  webhookUrl: "",
  webhookEnabled: false,
  rateLimitMinute: 60,
  rateLimitHour: 5000,
  lastTestStatus: null,
};

const uid = () => Math.random().toString(36).slice(2, 10);

function useLocalStore(key, initial) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [state]);

  return [state, setState];
}

/* -------------------- Toast Component -------------------- */
const Toast = ({ toast, onClose }) => {
  if (!toast) return null;
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div className="flex items-start gap-3 bg-white p-4 border shadow-lg rounded-xl max-w-sm">
        <CheckCircle className="text-teal-600" />
        <div>
          <div className="font-semibold">{toast.title}</div>
          <div className="text-sm text-gray-600">{toast.message}</div>
        </div>
        <button onClick={onClose} className="text-gray-400">
          <X />
        </button>
      </div>
    </div>
  );
};

export default function APIIntegrationSettings() {
  const [config, setConfig] = useLocalStore(STORAGE_KEY, defaults);
  const [newDomain, setNewDomain] = useState("");
  const [toast, setToast] = useState(null);
  const [importJson, setImportJson] = useState("");

  const update = (patch) => setConfig((prev) => ({ ...prev, ...patch }));

  /* -------------------- API Key Generator -------------------- */
  const generateKey = () => {
    const key = `api_${uid()}_${uid()}_${Date.now()}`;
    update({ apiKey: key });
    setToast({
      title: "API Key Generated",
      message: "Your new API key is ready.",
    });
  };

  const revokeKey = () => {
    if (!window.confirm("Revoke current API key? This action is permanent."))
      return;

    update({ apiKey: "" });
    setToast({
      title: "API Key Revoked",
      message: "Integration key removed.",
    });
  };

  /* -------------------- Domain Rule Management -------------------- */
  const addDomain = () => {
    if (!newDomain.trim()) return;
    if (config.domains.includes(newDomain))
      return setToast({
        title: "Duplicate Domain",
        message: "This domain is already added.",
      });

    update({ domains: [...config.domains, newDomain] });
    setNewDomain("");
  };

  const removeDomain = (dom) => {
    update({ domains: config.domains.filter((d) => d !== dom) });
  };

  /* -------------------- Test Webhook -------------------- */
  const testWebhook = () => {
    if (!config.webhookUrl)
      return setToast({
        title: "Webhook Missing",
        message: "Please configure a webhook URL first.",
      });

    // Simulated delivery success
    update({ lastTestStatus: "success" });
    setToast({
      title: "Test Delivered",
      message: "Webhook test sent successfully.",
    });
  };

  /* -------------------- Import / Export -------------------- */
  const exportConfig = () => {
    const json = JSON.stringify(config, null, 2);

    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "api-integration-settings.json";
    a.click();

    URL.revokeObjectURL(url);

    setToast({ title: "Exported", message: "Settings downloaded." });
  };

  const importConfig = () => {
    try {
      const data = JSON.parse(importJson);
      setConfig(data);
      setToast({
        title: "Imported",
        message: "Settings updated successfully.",
      });
    } catch (e) {
      setToast({
        title: "Invalid JSON",
        message: "Could not parse JSON input.",
      });
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50 font-roboto-poppins">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap');
          .font-roboto-poppins { font-family: "Roboto", "Poppins", sans-serif; }
        `}
      </style>

      <div className="max-w-6xl mx-auto">
        {/* ---------- Header ---------- */}
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
            <Server className="text-teal-600" />
            API Integration Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure API keys, allowed domains, webhook URLs, and rate limits.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* -----------------------------------------------------
               LEFT SIDE — MAIN SETTINGS
          ----------------------------------------------------- */}
          <div className="lg:col-span-2 space-y-6">
            {/* ---------------- API Key ---------------- */}
            <section className="p-5 bg-white rounded-xl shadow">
              <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Key className="text-teal-600" /> API Key
              </h2>

              <div className="flex items-center gap-3">
                <input
                  disabled
                  value={config.apiKey || "No API key generated"}
                  className="w-full p-2 border rounded bg-gray-100 text-gray-700"
                />
                <button
                  onClick={generateKey}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700"
                >
                  Generate
                </button>
                {config.apiKey && (
                  <button
                    onClick={revokeKey}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700"
                  >
                    Revoke
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Keep this key secure. Do not expose it in frontend code.
              </p>
            </section>

            {/* ---------------- Allowed Domains ---------------- */}
            <section className="p-5 bg-white rounded-xl shadow">
              <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Globe className="text-teal-600" /> Allowed Domains (CORS)
              </h2>

              <div className="flex gap-3 mb-3">
                <input
                  placeholder="https://yourdomain.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  className="flex-1 p-2 border rounded"
                />
                <button
                  onClick={addDomain}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  Add
                </button>
              </div>

              <ul className="space-y-2">
                {config.domains.map((dom) => (
                  <li
                    key={dom}
                    className="flex items-center justify-between bg-gray-50 p-2 rounded border"
                  >
                    <span>{dom}</span>
                    <button
                      onClick={() => removeDomain(dom)}
                      className="text-red-600 hover:bg-red-100 p-2 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {/* ---------------- Webhook ---------------- */}
            <section className="p-5 bg-white rounded-xl shadow">
              <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Webhook className="text-teal-600" /> Webhook Configuration
              </h2>

              <div>
                <label className="text-sm text-gray-600">Webhook URL</label>
                <input
                  type="text"
                  value={config.webhookUrl}
                  onChange={(e) => update({ webhookUrl: e.target.value })}
                  className="mt-1 w-full p-2 border rounded"
                  placeholder="https://yourapp.com/webhook"
                />
              </div>

              <div className="mt-3">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config.webhookEnabled}
                    onChange={(e) =>
                      update({ webhookEnabled: e.target.checked })
                    }
                  />
                  Enable Webhook Delivery
                </label>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={testWebhook}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  <RefreshCcw size={16} /> Test Webhook
                </button>
              </div>

              {config.lastTestStatus === "success" && (
                <p className="text-green-600 text-sm mt-3">
                  ✓ Last test delivered successfully.
                </p>
              )}
            </section>

            {/* ---------------- Rate Limits ---------------- */}
            <section className="p-5 bg-white rounded-xl shadow">
              <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Settings className="text-teal-600" /> Rate Limits
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Per Minute</label>
                  <input
                    type="number"
                    value={config.rateLimitMinute}
                    min={1}
                    onChange={(e) =>
                      update({
                        rateLimitMinute: Math.max(1, Number(e.target.value)),
                      })
                    }
                    className="mt-1 w-full p-2 border rounded"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600">Per Hour</label>
                  <input
                    type="number"
                    value={config.rateLimitHour}
                    min={1}
                    onChange={(e) =>
                      update({
                        rateLimitHour: Math.max(1, Number(e.target.value)),
                      })
                    }
                    className="mt-1 w-full p-2 border rounded"
                  />
                </div>
              </div>
            </section>

            {/* ---------------- Save ---------------- */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfig(defaults)}
                className="px-4 py-2 bg-gray-100 rounded"
              >
                Reset
              </button>
              <button
                onClick={() =>
                  setToast({
                    title: "Saved",
                    message: "API integration settings saved.",
                  })
                }
                className="px-4 py-2 bg-teal-600 text-white rounded"
              >
                Save Settings
              </button>
            </div>
          </div>

          {/* -----------------------------------------------------
               RIGHT SIDE — IMPORT / EXPORT
          ----------------------------------------------------- */}
          <aside className="space-y-6">
            {/* ---------- Export ---------- */}
            <div className="p-5 bg-white rounded-xl shadow">
              <h3 className="text-md font-semibold text-gray-700 flex items-center gap-2">
                <Download className="text-teal-600" /> Export Settings
              </h3>
              <p className="text-xs text-gray-500 mt-2">
                Download your configuration as JSON.
              </p>
              <button
                onClick={exportConfig}
                className="mt-3 w-full py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
              >
                Export JSON
              </button>
            </div>

            {/* ---------- Import ---------- */}
            <div className="p-5 bg-white rounded-xl shadow">
              <h3 className="text-md font-semibold text-gray-700 flex items-center gap-2">
                <Upload className="text-teal-600" /> Import Settings
              </h3>
              <textarea
                rows={6}
                placeholder="Paste JSON here"
                value={importJson}
                onChange={(e) => setImportJson(e.target.value)}
                className="mt-3 p-2 w-full border rounded"
              />
              <button
                onClick={importConfig}
                className="mt-3 w-full py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Import JSON
              </button>
            </div>
          </aside>
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
